import { getRunInputMessageContent, getRunInputMessages } from "../../businessModeService";

const MEETING_TRANSCRIPT_LABEL = "Meeting transcript";
const MANUAL_SELECTION_LABEL = "Page selection";
const MANUAL_FILE_ID_PREFIX = "manual::";

/**
 * Gmail API message id for in-thread reply drafting (digest file_id, not manual captures).
 *
 * @example
 * resolveReplyToMessageId({ file_id: "18abc123" });
 * // "18abc123"
 * resolveReplyToMessageId({ file_id: "manual::x" });
 * // ""
 */
export const resolveReplyToMessageId = (item) => {
  const fileId = String(item?.file_id || "").trim();
  if (!fileId || fileId.startsWith(MANUAL_FILE_ID_PREFIX)) return "";
  return fileId;
};

/**
 * Resolve stable performer ids for one action item row.
 *
 * `stableId` mirrors `getItemStableId` in actionItemHelpers.js: it prefers the
 * server-assigned `item_id`, which never changes for the life of the row, over the
 * workflow/key/file_id composite (`key` is a hash of the note text, so that composite
 * changes whenever the note is edited). The two must stay in lockstep — this is the id
 * `aiResultsByItem` and `performerRunStateByItem` get written and read under, so drift
 * between them makes those lookups miss and can surface a stale cached AI result during
 * the loading window right after "Try Again".
 *
 * @example
 * // Input — existing row with a server-assigned item_id
 * resolvePerformerItemIds({ item_id: "uuid-abc", action_item: "Email Bob", workflow_id: "wf-1", key: "k1", file_id: "f1" }, "");
 * // Output
 * { actionText: "Email Bob", workflowId: "wf-1", key: "k1", fileId: "f1", stableId: "uuid-abc" }
 * @example
 * // Input — optimistic placeholder with no item_id yet
 * resolvePerformerItemIds({ action_item: "Email Bob", workflow_id: "wf-1", key: "k1", file_id: "f1" }, "");
 * // Output
 * { actionText: "Email Bob", workflowId: "wf-1", key: "k1", fileId: "f1", stableId: "wf-1::k1::f1" }
 */
export const resolvePerformerItemIds = (item, contextWorkflowId = "") => {
  const actionText = String(item?.action_item || "").trim();
  const workflowId = String(contextWorkflowId || item?.workflow_id || "").trim();
  const key = String(item?.key || "").trim();
  const fileId = String(item?.file_id || "").trim();
  const itemId = String(item?.item_id || "").trim();
  const stableId =
    itemId || [workflowId, key, fileId].filter(Boolean).join("::") || actionText;
  return { actionText, workflowId, key, fileId, stableId };
};

/**
 * Extract performer-ready source text from a GCS blob (JSON payload or plain text).
 *
 * Manual-selection objects store `{source_type, body, selection_text, ...}`. Passing
 * that JSON through as a "Meeting transcript" dumps internal fields into drafts
 * and calendar notes.
 *
 * @example
 * extractSourceBodyFromGcsText('{"body":"Meet Abhinav","source_type":"manual_selection"}');
 * // "Meet Abhinav"
 * extractSourceBodyFromGcsText("Rep: Hello");
 * // "Rep: Hello"
 */
export const extractSourceBodyFromGcsText = (rawContent) => {
  const rawText = String(rawContent || "").trim();
  if (!rawText) return "";
  try {
    const parsed = JSON.parse(rawText);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return rawText;
    }
    for (const key of ["body", "current_message", "currentMessage", "text", "snippet", "selection_text"]) {
      const candidate = parsed[key];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  } catch {
    return rawText;
  }
  return rawText;
};

/**
 * Build performer context blocks from one source transcript payload.
 *
 * @example
 * // Input
 * buildMeetingContextBlocks("Rep: Hello");
 * // Output
 * [{ label: "Meeting transcript", content: "Rep: Hello", cache_scope: "run" }]
 *
 * @example
 * buildMeetingContextBlocks('{"body":"Meet Abhinav"}', { fileId: "manual::abc" });
 * // [{ label: "Page selection", content: "Meet Abhinav", cache_scope: "run" }]
 */
export const buildMeetingContextBlocks = (transcriptContent, options = {}) => {
  const content = extractSourceBodyFromGcsText(transcriptContent);
  if (!content) return [];
  const fileId = String(options?.fileId || "").trim();
  const label = fileId.startsWith(MANUAL_FILE_ID_PREFIX)
    ? MANUAL_SELECTION_LABEL
    : MEETING_TRANSCRIPT_LABEL;
  return [{ label, content, cache_scope: "run" }];
};

/**
 * Load raw source content for one action item's source message.
 *
 * The task row already carries ``source_gcs_url`` (the same URL the "Original source"
 * panel opens), so it is read first: no server-side source-table lookup that can fail
 * for a connector type it does not map. The lookup stays as the fallback for rows
 * without a stored URL.
 *
 * @example
 * // Input
 * await fetchSourceTranscriptContent("wf-1", "msg-123", { sourceGcsUrl: "gs://b/msg-123.vtt" });
 * // Output
 * "{...raw source payload...}"
 */
export const fetchSourceTranscriptContent = async (workflowId, fileId, { sourceGcsUrl = "" } = {}) => {
  const wf = String(workflowId || "").trim();
  const fid = String(fileId || "").trim();
  if (!wf || !fid) return "";

  // Manual captures resolve through the server (their stored URL is not the source text).
  const directUrl = fid.startsWith(MANUAL_FILE_ID_PREFIX) ? "" : String(sourceGcsUrl || "").trim();
  if (directUrl) {
    try {
      const res = await getRunInputMessageContent(wf, directUrl);
      const content = String(res?.content || "").trim();
      if (content) return content;
    } catch (_) {
      // Fall through to the server lookup.
    }
  }

  // A failure here is coded (source_unavailable) so callers map it to a plain sentence.
  const msgs = await getRunInputMessages(wf, { sourceMessageIds: [fid] });
  const list = Array.isArray(msgs) ? msgs : [];
  const gcsUrl = String(list[0]?.gcs_url || "").trim();
  if (!gcsUrl || gcsUrl === directUrl) return "";

  const res = await getRunInputMessageContent(wf, gcsUrl);
  return String(res?.content || "").trim();
};

/**
 * Build one performer API payload row with per-item meeting context.
 *
 * @example
 * // Input
 * await buildPerformerActionInput({ action_item: "Follow up", workflow_id: "wf-1", key: "k1", file_id: "f1" }, "", "draft-123", { revisionNotes: "shorter" });
 * // Output
 * { item: "Follow up", client_item_id: "wf-1::k1::f1", context_blocks: [...], gmail_draft_id: "draft-123", revision_notes: "shorter" }
 */
export const buildPerformerActionInput = async (item, contextWorkflowId = "", gmailDraftId = "", revisionMeta = {}) => {
  const { actionText, workflowId, key, fileId, stableId } = resolvePerformerItemIds(
    item,
    contextWorkflowId
  );
  const transcript = await fetchSourceTranscriptContent(workflowId, fileId, {
    sourceGcsUrl: item?.source_gcs_url,
  });
  const payload = {
    item: actionText,
    client_item_id: stableId,
    workflow_id: workflowId,
    key,
    file_id: fileId,
    context_blocks: buildMeetingContextBlocks(transcript, { fileId }),
  };
  const draftId = String(gmailDraftId || "").trim();
  if (draftId) payload.gmail_draft_id = draftId;
  const revisionNotes = String(revisionMeta?.revisionNotes || "").trim();
  const previousDraft = String(revisionMeta?.previousDraft || "").trim();
  const previousSubject = String(revisionMeta?.previousSubject || "").trim();
  if (revisionNotes) payload.revision_notes = revisionNotes;
  if (previousDraft) payload.previous_draft = previousDraft;
  if (previousSubject) payload.previous_subject = previousSubject;
  const replyToMessageId = resolveReplyToMessageId(item);
  if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
  const threadSubject = String(item?.title || "").trim();
  if (threadSubject) payload.thread_subject = threadSubject;
  const aiAssistType = String(item?.ai_assist_type || "").trim();
  // Don't pin "other" — Try Again must reclassify from the current notes.
  if (aiAssistType && aiAssistType.toLowerCase() !== "other") {
    payload.ai_assist_type = aiAssistType;
    const aiAssistReason = String(item?.ai_assist_reason || "").trim();
    if (aiAssistReason) payload.ai_assist_reason = aiAssistReason;
  }
  const actionCategory = String(item?.action_category || "").trim();
  if (actionCategory) payload.action_category = actionCategory;
  const sourceTimezone = String(item?.source_timezone || "").trim();
  if (sourceTimezone) payload.source_timezone = sourceTimezone;
  const timeConfidence = String(item?.time_confidence || "").trim();
  if (timeConfidence) payload.time_confidence = timeConfidence;
  if (item?.scheduled_start_at) payload.scheduled_start_at = item.scheduled_start_at;
  if (item?.scheduled_end_at) payload.scheduled_end_at = item.scheduled_end_at;
  if (item?.duration_minutes != null && item?.duration_minutes !== "") {
    const durationMinutes = Number(item.duration_minutes);
    if (Number.isFinite(durationMinutes) && durationMinutes > 0) {
      payload.duration_minutes = durationMinutes;
    }
  }
  return payload;
};
